import { useCallback } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import type { SessionUser } from 'src/shared/session';

// eslint-disable-next-line @change/sibling-module-imports -- direct import to avoid circular dependency
import { useModalConfirmationContext } from 'src/app/shared/components/payments/ModalConfirmationContext';
import { useUpdatePerimeterXUserData } from 'src/app/shared/hooks/perimeterX';
import { useCurrentUser } from 'src/app/shared/hooks/session';

type Props = { email: string; showContinueAsGuest: boolean; googleAuthEnabled: boolean };

export function useLoginWithModal(): (
	props?: Props,
) => Promise<{ user: SessionUser | undefined; continueAsGuest?: boolean } | undefined> {
	const user = useCurrentUser();
	const track = useTracking();
	const updatePerimeterXUserData = useUpdatePerimeterXUserData();

	const [, { displayModal }] = useModalConfirmationContext();

	return useCallback(
		// oxlint-disable-next-line complexity
		async (
			{ email, showContinueAsGuest, googleAuthEnabled } = {
				email: '',
				showContinueAsGuest: true,
				googleAuthEnabled: false,
			},
		) => {
			if (!user && !email) return undefined;
			const currentEmail = user?.email ?? email;
			void track('login_or_join_view', { context: 'log_in', user_flow: 'payment' });
			const modalResult = await displayModal({
				type: 'login',
				user,
				email: currentEmail,
				showContinueAsGuest,
				googleAuthEnabled,
			});
			if (!modalResult?.user) {
				void track('close_login_signup', {
					click_location: modalResult?.continueAsGuest ? 'continue_as_guest_click' : 'modal_close',
				});
			}
			if (modalResult?.user) await updatePerimeterXUserData();
			return modalResult;
		},
		[displayModal, user, track, updatePerimeterXUserData],
	);
}
